import { ConnectionId } from 'libs/end-to-end/src/connections/connections-config';
import { ConnectionsConfiguration } from 'libs/end-to-end/src/connections/connections-config';
import { ResolvedConnectionsConfiguration } from 'libs/end-to-end/src/connections/connections-config';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import { useOnChange } from 'apps/sigma2-ui/src/lib/hooks/use-on-change';
import { FountainInstallPayload } from 'apps/sigma2-ui/src/components/connections/FountainCredentialsForm';
import { FountainTestPayload } from 'apps/sigma2-ui/src/components/connections/FountainCredentialsForm';

type ConnectionsConfigurationState =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'failed'; errorMessage: string }
  | { state: 'succeeded'; config: ResolvedConnectionsConfiguration };

type UseConnectionsConfigurationResult = {
  state: ConnectionsConfigurationState['state'];
  config?: ResolvedConnectionsConfiguration;
  errorMessage?: string;

  refetch: () => Promise<void>;
  createOrUpdate: (next: ConnectionsConfiguration) => Promise<void>;

  installConnection: (connectionId: ConnectionId) => void;
  installGreenhouseConnection: (
    clientId: string,
    clientSecret: string,
  ) => Promise<void>;
  testGreenhouseConnection: (
    clientId: string,
    clientSecret: string,
  ) => Promise<{ success: boolean; error?: string }>;
  installLeverConnection: (apiKey: string) => Promise<void>;
  installFountainConnection: (payload: FountainInstallPayload) => Promise<void>;
  testFountainConnection: (
    payload: FountainTestPayload,
  ) => Promise<{ success: boolean; error?: string }>;
  installAshbyConnection: (apiKey: string) => Promise<void>;
  testAshbyConnection: (
    apiKey: string,
  ) => Promise<{ success: boolean; error?: string }>;
  installChartHopConnection: (apiKey: string) => Promise<void>;
  testChartHopConnection: (
    apiKey: string,
  ) => Promise<{ success: boolean; error?: string }>;
  uninstallConnection: (connectionId: ConnectionId) => Promise<void>;
};

export function useConnectionsConfiguration(
  organizationId?: string,
): UseConnectionsConfigurationResult {
  const { myUser, userAuthenticatedApiCall } = useUser();

  const [status, setStatus] = useState<ConnectionsConfigurationState>({
    state: 'idle',
  });

  const getConnectionsBaseUrl = useCallback(() => {
    if (!organizationId) {
      return;
    }

    return `${getApiBaseUrl()}/organizations/${organizationId}/connections`;
  }, [organizationId]);

  const refetch = useCallback(async () => {
    const connectionsBaseUrl = getConnectionsBaseUrl();
    if (!myUser || !connectionsBaseUrl) {
      setStatus({ state: 'idle' });
      return;
    }

    setStatus((prev) =>
      prev.state === 'succeeded'
        ? { state: 'loading', config: prev.config }
        : { state: 'loading' },
    );

    const response = await userAuthenticatedApiCall({
      url: `${connectionsBaseUrl}/configuration`,
      responseSchema: ResolvedConnectionsConfiguration,
    });

    if (!response || response.resultType === 'err') {
      setStatus({
        state: 'failed',
        errorMessage: 'Failed to fetch connections configuration',
      });
      return;
    }

    setStatus({
      state: 'succeeded',
      config: response.value,
    });
  }, [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall]);

  const createOrUpdate = useCallback(
    async (next: ConnectionsConfiguration) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        setStatus({ state: 'idle' });
        return;
      }

      setStatus((prev) =>
        prev.state === 'succeeded'
          ? { state: 'loading', config: prev.config }
          : { state: 'loading' },
      );

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/configuration`,
        requestOptions: {
          method: 'PUT',
          body: JSON.stringify(next),
        },
        responseSchema: ResolvedConnectionsConfiguration,
      });

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'failed',
          errorMessage: 'Failed to update connections configuration',
        });
        return;
      }

      setStatus({
        state: 'succeeded',
        config: response.value,
      });
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall],
  );

  const installConnection = useCallback(
    (connectionId: ConnectionId) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!connectionsBaseUrl) {
        return;
      }

      window.location.assign(
        `${connectionsBaseUrl}/${connectionId}/installation/oauth-install`,
      );
    },
    [getConnectionsBaseUrl],
  );

  const installGreenhouseConnection = useCallback(
    async (clientId: string, clientSecret: string) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return;
      }

      setStatus({ state: 'loading' });

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/greenhouse/installation`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ clientId, clientSecret }),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'failed',
          errorMessage: 'Failed to install Greenhouse connection',
        });
        return;
      }

      // Refetch to get updated installation status
      await refetch();
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall, refetch],
  );

  const testGreenhouseConnection = useCallback(
    async (
      clientId: string,
      clientSecret: string,
    ): Promise<{ success: boolean; error?: string }> => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/greenhouse/installation/test`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ clientId, clientSecret }),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        return {
          success: false,
          error:
            'Failed to connect to Greenhouse. Please check your credentials.',
        };
      }

      return { success: true };
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall],
  );

  const installLeverConnection = useCallback(
    async (apiKey: string) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return;
      }

      setStatus({ state: 'loading' });

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/lever/installation`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ apiKey }),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'failed',
          errorMessage: 'Failed to install Lever connection',
        });
        return;
      }

      await refetch();
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall, refetch],
  );

  const installFountainConnection = useCallback(
    async (payload: FountainInstallPayload) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return;
      }

      setStatus({ state: 'loading' });

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/fountain/installation`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'failed',
          errorMessage: 'Failed to install Fountain connection',
        });
        return;
      }

      await refetch();
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall, refetch],
  );

  const testFountainConnection = useCallback(
    async (
      payload: FountainTestPayload,
    ): Promise<{ success: boolean; error?: string }> => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/fountain/installation/test`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        const fallback =
          'Failed to connect to Fountain. Please check your credentials.';
        const reason =
          response?.resultType === 'err' &&
          typeof response.error.errorReason === 'string' &&
          response.error.errorReason.length > 0
            ? response.error.errorReason
            : fallback;
        return { success: false, error: reason };
      }

      return { success: true };
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall],
  );

  const installAshbyConnection = useCallback(
    async (apiKey: string) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return;
      }

      setStatus({ state: 'loading' });

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/ashby/installation`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ apiKey }),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'failed',
          errorMessage: 'Failed to install Ashby connection',
        });
        return;
      }

      await refetch();
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall, refetch],
  );

  const testAshbyConnection = useCallback(
    async (apiKey: string): Promise<{ success: boolean; error?: string }> => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/ashby/installation/test`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ apiKey }),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        const fallback =
          'Failed to connect to Ashby. Please check your API key.';
        const reason =
          response?.resultType === 'err' &&
          typeof response.error.errorReason === 'string' &&
          response.error.errorReason.length > 0
            ? response.error.errorReason
            : fallback;
        return { success: false, error: reason };
      }

      return { success: true };
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall],
  );

  const installChartHopConnection = useCallback(
    async (apiKey: string) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return;
      }

      setStatus({ state: 'loading' });

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/chartHop/installation`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ apiKey }),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'failed',
          errorMessage: 'Failed to install ChartHop connection',
        });
        return;
      }

      await refetch();
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall, refetch],
  );

  const testChartHopConnection = useCallback(
    async (apiKey: string): Promise<{ success: boolean; error?: string }> => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/chartHop/installation/test`,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify({ apiKey }),
        },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        const fallback =
          'Failed to connect to ChartHop. Please check your API key.';
        const reason =
          response?.resultType === 'err' &&
          typeof response.error.errorReason === 'string' &&
          response.error.errorReason.length > 0
            ? response.error.errorReason
            : fallback;
        return { success: false, error: reason };
      }

      return { success: true };
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall],
  );

  const uninstallConnection = useCallback(
    async (connectionId: ConnectionId) => {
      const connectionsBaseUrl = getConnectionsBaseUrl();
      if (!myUser || !connectionsBaseUrl) {
        return;
      }

      const prevConfig =
        status.state === 'succeeded' ? status.config : undefined;

      setStatus({ state: 'loading' });

      const response = await userAuthenticatedApiCall({
        url: `${connectionsBaseUrl}/${connectionId}/installation`,
        requestOptions: { method: 'DELETE' },
        responseSchema: GenericSuccess,
      });

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'failed',
          errorMessage: 'Failed to disconnect Slack',
        });
        return;
      }

      if (!prevConfig) {
        setStatus({ state: 'succeeded', config: { isEnabled: false } });
        return;
      }

      if (!prevConfig.isEnabled) {
        setStatus({ state: 'succeeded', config: prevConfig });
        return;
      }

      setStatus({
        state: 'succeeded',
        config: {
          ...prevConfig,
          connections: {
            ...prevConfig.connections,
            [connectionId]: {
              ...prevConfig.connections[connectionId],
              installation: { isInstalled: false },
            },
          },
        },
      });
    },
    [getConnectionsBaseUrl, myUser, userAuthenticatedApiCall, status],
  );

  useOnChange(
    useMemo(
      () => ({
        getConnectionsBaseUrl: getConnectionsBaseUrl(),
        myUser: myUser,
      }),
      [getConnectionsBaseUrl, myUser],
    ),
    ({ current }) => {
      if (!current.getConnectionsBaseUrl || !current.myUser) {
        setStatus({ state: 'loading' });
        return;
      }
      void refetch();
    },
    { enableInitialLoad: true },
  );

  return {
    state: status.state,
    config: status.state === 'succeeded' ? status.config : undefined,
    errorMessage: status.state === 'failed' ? status.errorMessage : undefined,

    refetch,
    createOrUpdate,
    installConnection,
    installGreenhouseConnection,
    testGreenhouseConnection,
    installLeverConnection,
    installFountainConnection,
    testFountainConnection,
    installAshbyConnection,
    testAshbyConnection,
    installChartHopConnection,
    testChartHopConnection,
    uninstallConnection,
  };
}
